import { DotsLoader } from 'Components/DotsLoader';
import { TextField } from 'Components/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { userAuth } from 'services/auth-service';
import { authenticatedFailure, authenticatedFinish, authenticatedSuccess, login, useAuthLoading } from 'store/auth';
import { getTenant } from 'utils/tenant_utils';
import {
  LoginPage,
  LoginCard,
  LogoWrapper,
  LoginHeading,
  FieldGroup,
  FieldLabel,
  FieldError,
  SubmitButton,
  FooterText,
  OtpRow,
  OTP_SX,
} from './styles';

export const VerifyCode = () => {
  const { t } = useTranslation(['user']);
  const { email } = useParams();
  const loading = useSelector(useAuthLoading);
  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: '',
    },
  });
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();
  const mutation = useMutation(
    ({ code, email }) => {
      dispatch(login());
      return userAuth(email, code, getTenant());
    },
    {
      onError: error => {
        dispatch(authenticatedFailure());
        setErrorMessage(error.response.data.message);
      },
      onSuccess: data => {
        setErrorMessage(null);
        dispatch(
          authenticatedSuccess({
            user: data.data.user,
            token: data.data.token,
            refreshToken: data.data.refreshToken,
          }),
        );
        try {
          const decoded = jwtDecode(data.data.token);
          if (decoded.preferredLanguage) {
            i18next.changeLanguage(decoded.preferredLanguage);
          }
        } catch (_) {
          // JWT decode failed — token may be malformed, continue anyway
        }
        setTimeout(() => {
          dispatch(authenticatedFinish());
          navigate(`/dashboard`);
        }, 4000);
      },
    },
  );

  const verif = async () => {
    const codes = getValues();
    const code = `${codes.code1}${codes.code2}${codes.code3}${codes.code4}${codes.code5}${codes.code6}`.substring(0, 6);

    mutation.mutate({ code, email });
  };
  const tabField = (value, nextField) => {
    if (!isNaN(value) && value !== undefined && value !== '') {
      document.getElementById(nextField).focus();
    }
  };
  const onPaste = value => {
    let contentPasted = value;
    if (value.length > 6) {
      contentPasted = value.substring(0, 6);
    }
    for (let i = 0; i < contentPasted.length; i++) {
      setValue(`code${i + 1}`, value.charAt(i));
    }
    document.getElementById(`code${contentPasted.length}`).focus();
  };

  const hasErrorsField = () => {
    return errors.code1 || errors.code2 || errors.code3 || errors.code4 || errors.code5 || errors.code6;
  };

  return (
    <form onSubmit={handleSubmit(verif)}>
      <LoginPage>
        <LoginCard>
          <LogoWrapper>
            <img src="/img/logo-horizontal.png" height="40" alt="Virtual Officer" style={{ objectFit: 'contain' }} />
          </LogoWrapper>
          <LoginHeading>
            <h1>{t('user:verifyCode.subtitle')}</h1>
            <p>{t('user:verifyCode.observation', { email })}</p>
          </LoginHeading>
          <FieldGroup>
            <FieldLabel>{t('user:verifyCode.codeLabel')}</FieldLabel>
            <OtpRow>
              <TextField
                size="sm"
                sx={OTP_SX}
                onKeyUp={e => tabField(e.target.value, 'code2')}
                onPaste={e => {
                  onPaste(e.clipboardData.getData('Text'));
                }}
                id="code1"
                {...register(`code1`, { required: true })}
                inputProps={{ minLength: 1, maxLength: 1, pattern: '[0-9]*', tabIndex: 1 }}
              />
              <TextField
                size="sm"
                sx={OTP_SX}
                onKeyUp={e => tabField(e.target.value, 'code3')}
                id="code2"
                {...register(`code2`, { required: true })}
                inputProps={{ minLength: 1, maxLength: 1, pattern: '[0-9]*', tabIndex: 2 }}
              />
              <TextField
                size="sm"
                sx={OTP_SX}
                onKeyUp={e => tabField(e.target.value, 'code4')}
                id="code3"
                {...register(`code3`, { required: true })}
                inputProps={{ minLength: 1, maxLength: 1, pattern: '[0-9]*', tabIndex: 3 }}
              />
              <TextField
                size="sm"
                sx={OTP_SX}
                onKeyUp={e => tabField(e.target.value, 'code5')}
                id="code4"
                {...register(`code4`, { required: true })}
                inputProps={{ minLength: 1, maxLength: 1, pattern: '[0-9]*', tabIndex: 4 }}
              />
              <TextField
                size="sm"
                sx={OTP_SX}
                onKeyUp={e => tabField(e.target.value, 'code6')}
                id="code5"
                {...register(`code5`, { required: true })}
                inputProps={{ minLength: 1, maxLength: 1, pattern: '[0-9]*', tabIndex: 5 }}
              />
              <TextField
                size="sm"
                sx={OTP_SX}
                id="code6"
                {...register(`code6`, { required: true })}
                inputProps={{ minLength: 1, maxLength: 1, pattern: '[0-9]*', tabIndex: 6 }}
              />
            </OtpRow>
            {hasErrorsField() && <FieldError>{t('user:verifyCode.validation.required')}</FieldError>}
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
          </FieldGroup>
          <SubmitButton type="submit" disabled={mutation.isLoading || loading || hasErrorsField()}>
            {mutation.isLoading || loading ? <DotsLoader /> : t('user:verifyCode.submit')}
          </SubmitButton>
          <FooterText>
            {t('user:verifyCode.noAccount')}{' '}
            <button type="button" onClick={() => navigate('/create-account')}>
              {t('user:verifyCode.signup')}
            </button>
          </FooterText>
        </LoginCard>
      </LoginPage>
    </form>
  );
};
